// @ts-check

/**
 * @file Renders the invoice groups index route.
 */

import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {useLocalSearchParams} from "expo-router"
import {memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native"
import buildRoutePath from "../../src/shared/build-route-path"
import Card from "../../src/components/card"
import EmptyState from "../../src/components/empty-state"
import {InvoiceGroup} from "../../src/frontend-models/invoice-group"
import Link from "../../src/components/link"
import LoadingState from "../../src/components/loading-state"
import Pagination from "../../src/components/pagination"
import errorMessage from "../../src/shared/error-message"
import useThemeColors, {useThemeName} from "../../src/shared/use-theme-colors"
import Text from "@kaspernj/api-maker/build/utils/text"
import ScreenTitle from "../../src/components/screen-title"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}
const PAGE_SIZE = 30

/** @typedef {{[key: string]: unknown}} InvoiceGroupsIndexScreenProps */

/**
 * @typedef {object} InvoiceGroupsIndexScreenState
 * @property {InvoiceGroup[]} invoiceGroups Loaded invoice group records.
 * @property {boolean} loading Whether invoice groups are loading.
 * @property {number} page Current page number.
 * @property {{kind: "error", text: string} | null} status Screen status message.
 * @property {number} totalCount Total invoice groups count.
 */

/**
 * Lists invoice groups with pagination.
 * @augments {ShapeComponent<InvoiceGroupsIndexScreenProps, InvoiceGroupsIndexScreenState>}
 */
class InvoiceGroupsIndexScreen extends ShapeComponent {
  loadRequestId = 0
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {InvoiceGroupsIndexScreenState} */
  state = {
    invoiceGroups: /** @type {InvoiceGroup[]} */ [],
    loading: true,
    page: 1,
    status: /** @type {{kind: "error", text: string} | null} */ null,
    totalCount: 0
  }

  /** Initializes translations, palette, and invoice group loading. */
  setup() {
    useLocale()
    this.params = useLocalSearchParams()
    this.colors = useThemeColors()
    this.themeName = useThemeName()

    const page = this.s.page
    useEffect(() => {
      void this.loadInvoiceGroups()
    }, [page])
  }

  /**
   * Renders the invoice groups index screen.
   * @returns {ReactNode} Invoice groups index content.
   */
  render() {
    const {loading, status} = this.s
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="invoiceGroupsIndexScreen"
      >
        <ScreenTitle>{_("Invoice groups")}</ScreenTitle>
        <View
          testID="invoiceGroupsIndexScreen/content"
        >
          <Text
            style={styles[`title-${this.tt.themeName}`] ||= {
              fontSize: 24,
              fontWeight: 700,
              marginBottom: 16
            }}
            testID="invoiceGroupsIndexScreen/title"
          >
            {_("Invoice groups")}
          </Text>
          {status &&
            <Text
              style={styles[`statusError-${this.tt.themeName}`] ||= {
                color: this.tt.colors.dangerText,
                fontWeight: 600,
                marginBottom: 16
              }}
              testID="invoiceGroupsIndexScreen/status"
            >
              {status.text}
            </Text>
          }
          {loading &&
            <LoadingState testID="invoiceGroupsIndexScreen/loadingText" />
          }
          {!loading && this.renderList()}
          {this.renderPagination()}
        </View>
      </View>
    )
  }

  /**
   * Renders loaded invoice groups or an empty state.
   * @returns {ReactNode} Invoice group list content.
   */
  renderList() {
    const {invoiceGroups} = this.s
    if (invoiceGroups.length === 0) {
      return (
        <EmptyState
          message={_("No invoice groups found")}
          testID="invoiceGroupsIndexScreen/emptyCard"
        />
      )
    }
    return (
      <View testID="invoiceGroupsIndexScreen/list">
        {invoiceGroups.map((invoiceGroup) => this.renderRow(invoiceGroup))}
      </View>
    )
  }

  /**
   * Renders an invoice group row.
   * @param {InvoiceGroup} invoiceGroup Invoice group to render.
   * @returns {ReactNode} Invoice group row.
   */
  renderRow(invoiceGroup) {
    const groupId = invoiceGroup.id()
    return (
      <Card
        key={groupId}
        style={styles.rowSpacing ||= {marginBottom: 12}}
        testID={`invoiceGroupsIndexScreen/row-${groupId}`}
      >
        <Link
          href={buildRoutePath(this.tt.params, `/invoice-groups/${groupId}`)}
          style={styles[`rowName-${this.tt.themeName}`] ||= {
            fontSize: 16,
            fontWeight: 600
          }}
          testID={`invoiceGroupsIndexScreen/row-${groupId}/name`}
        >
          {invoiceGroup.name()}
        </Link>
      </Card>
    )
  }

  /**
   * Renders pagination for loaded invoice groups.
   * @returns {ReactNode} Pagination controls or null while loading.
   */
  renderPagination() {
    const {loading, page, totalCount} = this.s
    if (loading) return null
    return (
      <Pagination
        onPageChange={this.tt.onPageChange}
        page={page}
        pageSize={PAGE_SIZE}
        testID="invoiceGroupsIndexScreen/pagination"
        totalCount={totalCount}
      />
    )
  }

  /**
   * Applies a new page number.
   * @param {number} nextPage Page to load.
   * @returns {void} Nothing.
   */
  onPageChange = (nextPage) => {
    if (nextPage === this.s.page) return
    this.s.page = nextPage
  }

  /**
   * Loads the current invoice group page.
   * @returns {Promise<void>} Resolves when invoice groups finish loading.
   * @rejects {unknown} When loading fails.
   */
  loadInvoiceGroups = async () => {
    const {page} = this.s
    const requestId = ++this.loadRequestId
    this.s.loading = true
    this.s.status = null
    try {
      const baseQuery = InvoiceGroup.all()
      const [invoiceGroups, totalCount] = await Promise.all([
        baseQuery.clone().page(page).perPage(PAGE_SIZE).toArray(),
        baseQuery.clone().count()
      ])
      if (this.loadRequestId !== requestId) return
      this.s.invoiceGroups = invoiceGroups
      this.s.loading = false
      this.s.totalCount = totalCount
    } catch (error) {
      if (this.loadRequestId !== requestId) return
      this.s.loading = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      throw error
    }
  }
}

/** @type {NamedExoticComponent<InvoiceGroupsIndexScreenProps>} */
const InvoiceGroupsIndexScreenComponent = memo(shapeComponent(InvoiceGroupsIndexScreen))

export default InvoiceGroupsIndexScreenComponent
